import { inject, Injectable } from '@angular/core';
import { TokenStorage } from 'app/core/auth/token-storage';
import { UserClaims } from 'app/core/user/user.types';

/**
 * The Salesforce `Account.Type` values whose members may see solicitations.
 *
 * Matched exactly, and exactly as the backend spells them in its own Account query — these are
 * picklist values, not free text, and the vocabulary is closed at three: 'Member',
 * 'Participating Member' and 'Government'. An unrecognized type is denied.
 */
const SOLICITATION_MEMBER_TYPES = ['Member', 'Government'];

/**
 * The `groups` claim carried by a member the ISAC Collaboration Forum has invited.
 *
 * The backend pushes it at sign-in from the Contact's Collaboration Forum checkbox, and it is the
 * same string the forum's documents are tagged with, which is why the constant is worth having:
 * the claim, the document tag and the tree the box renders are all one spelling.
 */
const COLLABORATION_FORUM_GROUP = 'COLLAB';

/**
 * Typed access to the signed-in member's claims.
 *
 * The session JWT carries the member's permissions, so the client answers `isAdmin()` and
 * friends by decoding it rather than by calling an endpoint. That decode used to be copied
 * into nine files as a file-level `getUser()`/`isAdmin()` pair, each one assuming a token was
 * present — `atob(undefined)` throws, which is why those components could not be constructed
 * without a real signed-in browser.
 *
 * Decoding here is total: a missing or malformed token yields empty claims, which read as
 * "no permissions" rather than as an exception.
 */
@Injectable({providedIn: 'root'})
export class CurrentUserService
{
    private readonly _tokenStorage = inject(TokenStorage);

    /** The decoded token payload, or `{}` when there is no usable token. */
    get claims(): UserClaims
    {
        const payload = this._tokenStorage.token.split('.')[1];

        if ( !payload )
        {
            return {};
        }

        try
        {
            return JSON.parse(atob(payload)) as UserClaims;
        }
        catch
        {
            return {};
        }
    }

    get id(): string
    {
        return this.claims.id ?? '';
    }

    get name(): string
    {
        return this.claims.name ?? '';
    }

    get email(): string
    {
        return this.claims.email ?? '';
    }

    /** The Salesforce account this member belongs to — the fileshare is keyed on it. */
    get memberId(): string
    {
        return this.claims.memberId ?? '';
    }

    get groups(): string[]
    {
        return this.claims.groups ?? [];
    }

    /**
     * The named capabilities this session holds beyond `admin`, resolved from the organization's
     * Salesforce Account. Absent reads as none, like every other claim here.
     */
    get roles(): string[]
    {
        return this.claims.roles ?? [];
    }

    /**
     * The organization's Salesforce `Account.Type`, or undefined when the token predates the
     * claim. Undefined is meaningful here — see `canViewSolicitations` — so unlike the string
     * getters above this one does not flatten it to `''`.
     */
    get membershipType(): string | undefined
    {
        return this.claims.membershipType;
    }

    isAdmin(): boolean
    {
        return this.claims.admin === true;
    }

    isFullMember(): boolean
    {
        return this.claims.isFullMember === true;
    }

    hasNda(): boolean
    {
        return this.claims.hasNda === true;
    }

    /**
     * The only thing gating CUI documents. The server omits CUI records from its responses for a
     * caller without it, so this is for presentation, never for filtering a payload.
     */
    canViewCui(): boolean
    {
        return this.claims.canViewCui === true;
    }

    /** True when this session holds `roleName` — see `roles`. */
    hasRole(roleName: string): boolean
    {
        return this.roles.indexOf(roleName) >= 0;
    }

    /**
     * The support role: an organization whose Salesforce Account has Support Role checked, whose
     * members may unlock an account that has lost its second factor.
     *
     * Deliberately not a kind of admin. It is additive and narrow — it admits the account search
     * and the MFA reset and nothing else, and in particular has no bearing on CUI, which
     * `canViewCui` gates alone.
     */
    isSupport(): boolean
    {
        return this.hasRole('support');
    }

    /**
     * Who may reach the User Management screen: an admin, or a support user.
     *
     * The matching endpoints (`GET /api/user` and `DELETE /api/users/:userId/mfa`) are
     * `requiresAdminOrRole('support')` on the backend, which is what actually refuses; this keeps
     * everyone else off a page that would answer 403 to everything on it.
     */
    canManageUsers(): boolean
    {
        return this.isAdmin() || this.isSupport();
    }

    /**
     * Who may export every account's address as a CSV — admins only, deliberately.
     *
     * Support is scoped to unlocking a member who has asked for it, which is the search on that
     * same screen, not bulk export; `GET /api/user/list` is still `requiresAdmin()`. Named here
     * rather than spelled `isAdmin()` in the template so that if support's scope ever changes
     * there is one line to move.
     */
    canDownloadUserList(): boolean
    {
        return this.isAdmin();
    }

    /** A full member sees every working group; anyone else sees the ones they belong to. */
    canSeeWorkingGroups(): boolean
    {
        return this.isFullMember() || this.groups.length > 0;
    }

    /**
     * Who may see the solicitations list and any one solicitation.
     *
     * Member and Government organizations, yes; Participating Member, no. Admins always, because
     * they sit in one specific Salesforce Account whose `Type` is not necessarily either of the
     * permitted two — without this an admin could edit a solicitation but not reach the list.
     *
     * This is the single definition of that rule: `permissionGuard` on the routes and the nav
     * link in the classy layout both ask this, so there is nowhere for the two to disagree.
     * Before it existed the rule lived only in an `*ngIf` on that nav link, which hid the link
     * without gating the routes — anyone signed in could reach the pages by URL.
     *
     * Note this is deliberately *not* `isFullMember()`. That claim is `Membership_Status__c ===
     * 'Active'`, a different Salesforce field whose values overlap the type vocabulary, so it
     * cannot separate a Participating Member from a Member.
     */
    canViewSolicitations(): boolean
    {
        if ( this.isAdmin() )
        {
            return true;
        }

        // Transitional, removable once every token predating the membershipType claim has
        // expired — they last 24h. Falling back to the old rule keeps a signed-in member from
        // losing the page mid-session; it is not stricter than what shipped before it.
        //
        // This reads a missing claim, not an empty one: a token carrying membershipType: null or
        // '' is a real answer and is denied. The one gap is an account whose Salesforce Type is
        // undefined, which is dropped at signing and so looks like an old token — delete this
        // branch and that closes too.
        if ( this.membershipType === undefined )
        {
            return this.isFullMember();
        }

        return SOLICITATION_MEMBER_TYPES.includes(this.membershipType);
    }

    /**
     * Who may create or edit a solicitation.
     *
     * The matching `/api/solicitations` writes are `requiresAdmin()` on the backend, so this
     * keeps a non-admin out of a form whose save would 403.
     */
    canEditSolicitations(): boolean
    {
        return this.isAdmin();
    }

    /**
     * Whether this session's password is past the server's expiry window and must be changed.
     *
     * The server decides — it holds both the window and the date the password was set, and it
     * bakes the verdict into the token at sign-in. Nothing here computes an age, and nothing here
     * is the enforcement: every authenticated endpoint refuses an expired session except
     * /api/auth/change-password. `AuthGuard` reads this only so the member lands on the screen
     * that fixes it instead of on a wall of 403s.
     *
     * Absent reads as not expired, like every other claim: a token minted before the claim
     * existed carries nothing here, and treating that as expired would bounce every live session
     * to the change-password screen on deploy.
     */
    passwordExpired(): boolean
    {
        return this.claims.passwordExpired === true;
    }

    /**
     * Whether this member is their organization's primary contact in Salesforce. Shown under
     * their name in the sidebar and nowhere else — it confers nothing.
     *
     * Frozen into the token like every other claim, so a change of contact in Salesforce shows at
     * the next sign-in. Absent, as on a token minted before the claim existed, reads as not.
     */
    isPrimaryContact(): boolean
    {
        return this.claims.isPrimaryContact === true;
    }

    /** True when the member belongs to `groupName`, or is an admin and so sees everything. */
    isInGroup(groupName: string): boolean
    {
        return this.groups.indexOf(groupName) >= 0;
    }

    /**
     * Who may see the ISAC Collaboration Forum and its documents.
     *
     * Every other working group box on that page shows to any full member, on the reasoning that
     * the groups are open and the box is an invitation to join one. The forum is not: it is a
     * named cohort of RAPID awardees, so being a full member is deliberately not enough and the
     * `COLLAB` claim — Salesforce's Collaboration Forum checkbox on the Contact — is the whole
     * rule. Admins are admitted because they are who files materials into the forum's folder.
     *
     * Named here rather than spelled out on the box so the page and anything that later gates on
     * the forum cannot disagree. Note this is a *display* rule, not a boundary: the backend serves
     * the COLLAB tree and its documents to any signed-in caller, exactly as it does for every
     * other working group tag. Hiding the box keeps the forum's materials off the page for
     * everyone else; it does not withhold them from someone who asks the API directly.
     */
    canSeeCollaborationForum(): boolean
    {
        return this.isAdmin() || this.isInGroup(COLLABORATION_FORUM_GROUP);
    }
}
